import React, { useState, useEffect } from 'react';
import { 
    Row, Col, Card, CardBody, Button, Table, Badge, Input, Form, FormGroup, Label, CardHeader,
    Nav, NavItem, NavLink, TabContent, TabPane 
} from 'reactstrap';
import { useToasts } from 'react-toast-notifications';
import ReactHTMLTableToExcel from 'react-html-table-to-excel';
import { withTranslation } from 'react-multi-lang';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faUsers, 
    faCalendarAlt, 
    faHistory, 
    faList, 
    faCalendarWeek, 
    faXmark 
} from '@fortawesome/free-solid-svg-icons';
import EssService from '../Services/EssService';
import UsersService from '../Services/UsersService';
import AuthService from '../Services/AuthService';

const formatDateString = (val) => {
    if (!val) return '';
    if (typeof val === 'string') {
        return val.substring(0, 10);
    }
    const d = new Date(val);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const formatGreekDate = (val) => {
    if (!val) return '';
    if (typeof val === 'string') {
        const parts = val.substring(0, 10).split('-');
        if (parts.length === 3) {
            return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
    }
    const d = new Date(val);
    if (isNaN(d.getTime())) return val;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${day}/${month}/${year}`;
};

const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
        case 'approved': return 'success';
        case 'rejected': return 'danger';
        case 'cancelled': return 'secondary';
        default: return 'warning';
    }
};

const EssManager = ({ t }) => {
    const { addToast } = useToasts();
    const essService = new EssService();
    const usersService = new UsersService();
    const authService = new AuthService();

    const [currentWhCode, setCurrentWhCode] = useState('');
    const [employees, setEmployees] = useState([]);
    const [leaveRequests, setLeaveRequests] = useState([]);
    const [advanceRequests, setAdvanceRequests] = useState([]);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [rejectionReason, setRejectionReason] = useState('');
    const [headroomData, setHeadroomData] = useState([]);
    const [loadingHeadroom, setLoadingHeadroom] = useState(false);

    // Main navigation tabs: 'weekly' vs 'requests'
    const [mainTab, setMainTab] = useState('weekly');
    const [userViewMode, setUserViewMode] = useState('timeline'); // 'timeline' or 'table'

    const [activeTab, setActiveTab] = useState('pending');
    const [leaveSearch, setLeaveSearch] = useState('');
    const [leaveStatusFilter, setLeaveStatusFilter] = useState('all');
    const [advanceSearch, setAdvanceSearch] = useState('');
    const [advanceStatusFilter, setAdvanceStatusFilter] = useState('all');

    const [warehouseFilter, setWarehouseFilter] = useState('all');
    const [specialtyFilter, setSpecialtyFilter] = useState('all');
    const [companyFilter, setCompanyFilter] = useState('all');
    const [managerFilter, setManagerFilter] = useState('all');
    const [historyUserId, setHistoryUserId] = useState('all');

    const allWarehouses = [...new Set([
        ...(authService.availableConfigs || []).map(c => c.whCode),
        ...employees.map(e => e.whs).filter(Boolean).flatMap(w => w.split(',').map(s => s.trim()))
    ])].filter(Boolean);

    const getWarehouseLabel = (whCode) => {
        if (!whCode) return '-';
        const config = (authService.availableConfigs || []).find(c => c.whCode === whCode);
        return config ? `${config.name} (${whCode})` : `Warehouse ${whCode}`;
    };

    const allSpecialties = [...new Set(employees.map(e => e.specialty).filter(Boolean))];
    const allCompanies = ['NORDIA', 'NORDIA ENERGY', 'ΜΑΡΜΑΡΑ ΔΙΟΝΥΣΟΥ'];
    const allManagers = [...new Set(employees.map(e => e.manager).filter(Boolean))];

    const isHrOrAdmin = () => {
        const u = authService.getUser();
        if (!u) return false;
        const hrOrAdminRoles = ['admin', 'hrmanager', 'hrmanagement'];
        const r1 = (u.role || '').toLowerCase();
        const r2 = (u.role2 || '').toLowerCase();
        return hrOrAdminRoles.includes(r1) || hrOrAdminRoles.includes(r2);
    };

    const userMatchesFilters = (u) => {
        const hasSpecificFilter = warehouseFilter !== 'all' || specialtyFilter !== 'all' || companyFilter !== 'all' || managerFilter !== 'all';
        if (!u) return !hasSpecificFilter;
        if (warehouseFilter !== 'all') {
            if (!u.whs) return false;
            const whList = u.whs.split(',').map(s => s.trim());
            if (!whList.includes(warehouseFilter) && u.whs !== warehouseFilter) return false;
        }
        if (specialtyFilter !== 'all' && u.specialty !== specialtyFilter) return false;
        if (companyFilter !== 'all' && (u.company || '').trim() !== companyFilter) return false;
        if (managerFilter !== 'all' && u.manager !== managerFilter) return false;
        return true;
    };

    const filteredEmployees = employees.filter(userMatchesFilters);

    const filteredLeaveRequests = leaveRequests.filter(lr => {
        const u = lr.user || employees.find(e => e.userId === lr.userId);
        if (!userMatchesFilters(u)) return false;
        const matchesStatus = leaveStatusFilter === 'all' || lr.status === leaveStatusFilter;
        const searchLower = leaveSearch.toLowerCase();
        const matchesSearch = !leaveSearch || 
            (u?.name && u.name.toLowerCase().includes(searchLower)) ||
            (lr.user?.name && lr.user.name.toLowerCase().includes(searchLower)) ||
            (lr.specialty && lr.specialty.toLowerCase().includes(searchLower)) ||
            (lr.leaveType?.name && lr.leaveType.name.toLowerCase().includes(searchLower)) ||
            (lr.reason && lr.reason.toLowerCase().includes(searchLower));
        return matchesStatus && matchesSearch;
    });

    const filteredAdvanceRequests = advanceRequests.filter(ar => {
        const u = ar.user || employees.find(e => e.userId === ar.userId);
        if (!userMatchesFilters(u)) return false;
        const matchesStatus = advanceStatusFilter === 'all' || ar.status === advanceStatusFilter;
        const searchLower = advanceSearch.toLowerCase();
        const matchesSearch = !advanceSearch || 
            (u?.name && u.name.toLowerCase().includes(searchLower)) ||
            (ar.user?.name && ar.user.name.toLowerCase().includes(searchLower)) ||
            (ar.notes && ar.notes.toLowerCase().includes(searchLower)) ||
            (ar.paymentComment && ar.paymentComment.toLowerCase().includes(searchLower)) ||
            (ar.amount && ar.amount.toString().includes(searchLower));
        return matchesStatus && matchesSearch;
    });

    const pendingLeaves = leaveRequests.filter(r => {
        const u = r.user || employees.find(e => e.userId === r.userId);
        return r.status === 'pending' && userMatchesFilters(u);
    });
    const pendingAdvances = advanceRequests.filter(r => {
        const u = r.user || employees.find(e => e.userId === r.userId);
        return r.status === 'pending' && userMatchesFilters(u);
    });
    const totalPending = pendingLeaves.length + pendingAdvances.length;

    // Week selector: Monday of the selected week
    const [weekStartDate, setWeekStartDate] = useState(() => {
        const d = new Date();
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
        const mon = new Date(d.setDate(diff));
        return formatDateString(mon);
    });

    useEffect(() => {
        const config = authService.getConfig();
        if (config && config.whCode) {
            setCurrentWhCode(config.whCode);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [currentWhCode, weekStartDate]);

    useEffect(() => {
        if (selectedRequest && selectedRequest.specialty && selectedRequest.startDate && selectedRequest.endDate) {
            fetchHeadroom();
        } else {
            setHeadroomData([]);
        }
    }, [selectedRequest]);

    const fetchData = () => {
        const userIsHrOrAdmin = isHrOrAdmin();

        // Fetch all leave requests
        essService.getAllLeaveRequests()
            .then(res => {
                // When admin or hrManager is logged in, do not filter by wh; display everyone.
                const filtered = (!userIsHrOrAdmin && currentWhCode)
                    ? res.data.filter(lr => lr.user && lr.user.whs === currentWhCode)
                    : res.data;
                setLeaveRequests(filtered);
            })
            .catch(e => {
                console.error(e);
                addToast(t('ess.failLoadLeaveRequests') || 'Failed to load leave requests', { appearance: 'error' });
            });

        // Fetch all advance requests
        essService.getAllAdvancePayments()
            .then(res => {
                // When admin or hrManager is logged in, do not filter by wh; display everyone.
                const filtered = (!userIsHrOrAdmin && currentWhCode)
                    ? res.data.filter(ar => ar.user && ar.user.whs === currentWhCode)
                    : res.data;
                setAdvanceRequests(filtered);
            })
            .catch(e => {
                console.error(e);
                addToast(t('ess.failLoadAdvancePayments') || 'Failed to load advance payments', { appearance: 'error' });
            });

        // Fetch employees
        usersService.getUsers()
            .then(users => {
                // When admin or hrManager is logged in, do not filter by wh; display everyone.
                const filtered = (!userIsHrOrAdmin && currentWhCode)
                    ? users.filter(u => u.whs === currentWhCode)
                    : users;
                setEmployees(filtered);
            })
            .catch(e => console.error(e));
    };

    const fetchHeadroom = () => {
        setLoadingHeadroom(true);
        essService.getHeadroom(
            selectedRequest.specialty,
            (selectedRequest.user && selectedRequest.user.whs) || currentWhCode || '',
            formatDateString(selectedRequest.startDate),
            formatDateString(selectedRequest.endDate)
        )
            .then(res => {
                setHeadroomData(res.data);
                setLoadingHeadroom(false);
            })
            .catch(e => {
                console.error(e);
                setLoadingHeadroom(false);
            });
    };

    // Week navigation helpers
    const handlePrevWeek = () => {
        const d = new Date(weekStartDate);
        d.setDate(d.getDate() - 7);
        setWeekStartDate(formatDateString(d));
    };

    const handleNextWeek = () => {
        const d = new Date(weekStartDate);
        d.setDate(d.getDate() + 7);
        setWeekStartDate(formatDateString(d));
    };

    // Calculate dates of current week
    const getWeekDays = () => {
        const days = [];
        const base = new Date(weekStartDate);
        for (let i = 0; i < 7; i++) {
            const next = new Date(base);
            next.setDate(base.getDate() + i);
            days.push({
                dateStr: formatDateString(next),
                label: next.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'numeric' })
            });
        }
        return days;
    };

    const weekDays = getWeekDays();

    // Leave request matching for calendar cell
    const findOverlapLeave = (userId, dateStr) => {
        const target = new Date(dateStr);
        return leaveRequests.find(lr => {
            if (lr.userId !== userId) return false;
            if (lr.status !== 'approved' && lr.status !== 'pending') return false;
            const start = new Date(formatDateString(lr.startDate));
            const end = new Date(formatDateString(lr.endDate));
            return start <= target && target <= end;
        });
    };

    const handleApprove = (req) => {
        const isLeave = !!req.leaveTypeId;
        const promise = isLeave 
            ? essService.approveLeaveRequest(req.id)
            : essService.approveAdvancePayment(req.id);

        promise
            .then(() => {
                addToast(t('ess.requestApprovedSuccess') || 'Request approved successfully!', { appearance: 'success' });
                setSelectedRequest(null);
                fetchData();
            })
            .catch(err => {
                console.error(err);
                addToast(t('ess.approvalFailed') || 'Approval failed.', { appearance: 'error' });
            });
    };

    const handleReject = (req) => {
        if (!rejectionReason) {
            addToast(t('ess.provideRejectionReason') || 'Please provide a rejection reason.', { appearance: 'warning' });
            return;
        }
        const isLeave = !!req.leaveTypeId;
        const promise = isLeave 
            ? essService.rejectLeaveRequest(req.id, rejectionReason)
            : essService.rejectAdvancePayment(req.id, rejectionReason);

        promise
            .then(() => {
                addToast(t('ess.requestRejected') || 'Request rejected.', { appearance: 'info' });
                setSelectedRequest(null);
                setRejectionReason('');
                fetchData();
            })
            .catch(err => {
                console.error(err);
                addToast(t('ess.rejectionFailed') || 'Rejection failed.', { appearance: 'error' });
            });
    };

    return (
        <div style={{ marginTop: '20px' }}>
            <h2 className="mb-4">{t('ess.essManagerTitle') || 'Employee Self Service Manager'}</h2>
            
            {/* Filters Bar */}
            <Card className="mb-3 p-3 bg-light">
                <Row className="g-3 align-items-center">
                    <Col md={3}>
                        <FormGroup className="mb-0">
                            <Label className="small fw-bold">{t('ess.filterByWarehouse') || 'Filter by Warehouse'}</Label>
                            <Input 
                                type="select" 
                                bsSize="sm" 
                                value={warehouseFilter} 
                                onChange={e => setWarehouseFilter(e.target.value)}
                            >
                                <option value="all">{t('ess.allWarehouses') || 'All Warehouses'}</option>
                                {allWarehouses.map(wh => (
                                    <option key={wh} value={wh}>{getWarehouseLabel(wh)}</option>
                                ))}
                            </Input>
                        </FormGroup>
                    </Col>
                    <Col md={3}>
                        <FormGroup className="mb-0">
                            <Label className="small fw-bold">{t('ess.filterBySpecialty') || 'Filter by Specialty'}</Label>
                            <Input type="select" bsSize="sm" value={specialtyFilter} onChange={e => setSpecialtyFilter(e.target.value)}>
                                <option value="all">{t('ess.allSpecialties') || 'All Specialties'}</option>
                                {allSpecialties.map(s => <option key={s} value={s}>{s}</option>)}
                            </Input>
                        </FormGroup>
                    </Col>
                    <Col md={3}>
                        <FormGroup className="mb-0">
                            <Label className="small fw-bold">{t('ess.filterByCompany') || 'Filter by Company'}</Label>
                            <Input type="select" bsSize="sm" value={companyFilter} onChange={e => setCompanyFilter(e.target.value)}>
                                <option value="all">{t('ess.allCompanies') || 'All Companies'}</option>
                                {allCompanies.map(c => <option key={c} value={c}>{c}</option>)}
                            </Input>
                        </FormGroup>
                    </Col>
                    <Col md={3}>
                        <FormGroup className="mb-0">
                            <Label className="small fw-bold">{t('ess.filterByManager') || 'Filter by Manager'}</Label>
                            <Input type="select" bsSize="sm" value={managerFilter} onChange={e => setManagerFilter(e.target.value)}>
                                <option value="all">{t('ess.allManagers') || 'All Managers'}</option>
                                {allManagers.map(m => <option key={m} value={m}>{m}</option>)}
                            </Input>
                        </FormGroup>
                    </Col>
                </Row>
                {(warehouseFilter !== 'all' || specialtyFilter !== 'all' || companyFilter !== 'all' || managerFilter !== 'all') && (
                    <div className="mt-2 text-right text-end">
                        <Button 
                            color="link" 
                            size="sm" 
                            className="text-danger p-0 text-decoration-none"
                            onClick={() => {
                                setWarehouseFilter('all');
                                setSpecialtyFilter('all');
                                setCompanyFilter('all');
                                setManagerFilter('all');
                            }}
                        >
                            {t('ess.resetAllFilters') || 'Reset all filters'}
                        </Button>
                    </div>
                )}
            </Card>

            {/* Main Tabs Navigation: Weekly View vs Requests */}
            <Nav tabs className="mb-3" style={{ borderBottom: '2px solid #dee2e6' }}>
                <NavItem>
                    <NavLink
                        className={mainTab === 'weekly' ? 'active font-weight-bold' : ''}
                        style={{
                            cursor: 'pointer',
                            fontSize: '1rem',
                            padding: '10px 24px',
                            borderTop: mainTab === 'weekly' ? '3px solid #007bff' : undefined
                        }}
                        onClick={() => setMainTab('weekly')}
                    >
                        <FontAwesomeIcon icon={faCalendarWeek} className="mr-2 me-2" />
                        {t('ess.weeklyView') || 'Weekly View'}
                    </NavLink>
                </NavItem>
                <NavItem>
                    <NavLink
                        className={mainTab === 'requests' ? 'active font-weight-bold' : ''}
                        style={{
                            cursor: 'pointer',
                            fontSize: '1rem',
                            padding: '10px 24px',
                            borderTop: mainTab === 'requests' ? '3px solid #007bff' : undefined
                        }}
                        onClick={() => setMainTab('requests')}
                    >
                        <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 me-2" />
                        {t('ess.requests') || 'Requests'}
                        {totalPending > 0 && (
                            <Badge color="warning" pill className="ml-2 ms-2">
                                {totalPending} {t('ess.pending')?.toLowerCase() || 'pending'}
                            </Badge>
                        )}
                    </NavLink>
                </NavItem>
            </Nav>

            <Row>
                {/* Main timeline & request list */}
                <Col lg={8} md={12}>
                    {mainTab === 'weekly' ? (
                        <Card className="mb-4">
                            <CardHeader className="d-flex justify-content-between align-items-center flex-wrap">
                                <div className="d-flex align-items-center">
                                    <strong>{t('ess.weeklyTimeline') || 'Weekly Teams-Style Timeline'}</strong>
                                    <div className="btn-group btn-group-sm ms-3 ml-3" role="group">
                                        <Button 
                                            color={userViewMode === 'timeline' ? 'primary' : 'outline-primary'} 
                                            size="sm"
                                            onClick={() => setUserViewMode('timeline')}
                                        >
                                            <FontAwesomeIcon icon={faCalendarWeek} className="me-1 mr-1" /> {t('ess.timeline') || 'Timeline'}
                                        </Button>
                                        <Button 
                                            color={userViewMode === 'table' ? 'primary' : 'outline-primary'} 
                                            size="sm"
                                            onClick={() => setUserViewMode('table')}
                                        >
                                            <FontAwesomeIcon icon={faList} className="me-1 mr-1" /> {t('ess.directory') || 'Directory'} ({filteredEmployees.length})
                                        </Button>
                                    </div>
                                </div>
                                {userViewMode === 'timeline' ? (
                                    <div className=" mt-2 mt-sm-0">
                                        <br />
                                        <Button size="sm" color="secondary" onClick={handlePrevWeek} className="mr-2 me-2">&lt; {t('schedules.previous') || 'Prev'}</Button>
                                        <span className="mx-2 font-weight-bold">{formatGreekDate(weekStartDate)}</span>
                                        <Button size="sm" color="secondary" onClick={handleNextWeek} className="ml-2 ms-2 mr-3 me-3">{t('schedules.next') || 'Next'} &gt;</Button>
                                        <ReactHTMLTableToExcel
                                            id="timelineExcelBtn"
                                            className="btn btn-sm btn-success fw-bold"
                                            table="timelineTable"
                                            filename={`timeline-${weekStartDate}`}
                                            sheet="Timeline"
                                            buttonText={t('ess.exportExcel') || 'Export to Excel'}
                                        />
                                    </div>
                                ) : (
                                    <div className="mt-2 mt-sm-0">
                                        <ReactHTMLTableToExcel
                                            id="usersListExcelBtn"
                                            className="btn btn-sm btn-success fw-bold"
                                            table="usersListTable"
                                            filename="employees_directory"
                                            sheet="Employees"
                                            buttonText={t('ess.exportExcel') || 'Export to Excel'}
                                        />
                                    </div>
                                )}
                            </CardHeader>
                            <CardBody className="p-0">
                                {userViewMode === 'timeline' ? (
                                    <Table bordered responsive id="timelineTable" style={{ margin: 0, tableLayout: 'fixed' }}>
                                        <thead className="bg-light">
                                            <tr>
                                                <th style={{ width: '190px', whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'break-word' }}>{t('ess.employee') || 'Employee'}</th>
                                                {weekDays.map(day => (
                                                    <th key={day.dateStr} className="text-center" style={{ fontSize: '0.85rem', whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                                                        {day.label}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredEmployees.length === 0 ? (
                                                <tr>
                                                    <td colSpan={8} className="text-center text-muted py-4">
                                                        {t('ess.noEmployeesFound') || 'No employees found matching filters.'}
                                                    </td>
                                                </tr>
                                            ) : (
                                                filteredEmployees.map(emp => (
                                                    <tr key={emp.userId}>
                                                        <td style={{ whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                                                            <div>
                                                                <strong>{emp.name}</strong>
                                                            </div>
                                                            <div className="d-flex justify-content-between align-items-center flex-wrap">
                                                                <span className="text-muted" style={{ fontSize: '0.75rem', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                                                                    {emp.specialty || t('ess.noSpecialty') || 'No Specialty'}
                                                                </span>
                                                                <Button 
                                                                    size="sm" 
                                                                    color="link" 
                                                                    className="p-0 text-primary ms-1 ml-1" 
                                                                    style={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }} 
                                                                    title="View Request History" 
                                                                    onClick={() => { 
                                                                        setHistoryUserId(emp.userId); 
                                                                        setActiveTab('history'); 
                                                                        setMainTab('requests'); 
                                                                    }}
                                                                >
                                                                    {t('ess.history') || 'History'}
                                                                </Button>
                                                            </div>
                                                        </td>
                                                        {weekDays.map(day => {
                                                            const overlap = findOverlapLeave(emp.userId, day.dateStr);
                                                            if (!overlap) return <td key={day.dateStr}></td>;

                                                            const isPending = overlap.status === 'pending';
                                                            const cellStyle = isPending
                                                                ? {
                                                                    border: "2px dotted #ffc107",
                                                                    backgroundColor: "#fff3cd",
                                                                    color: "#856404",
                                                                    cursor: "pointer",
                                                                    textAlign: "center",
                                                                    verticalAlign: "middle",
                                                                    padding: "4px",
                                                                    whiteSpace: "normal",
                                                                    wordBreak: "break-word",
                                                                    overflowWrap: "break-word"
                                                                  }
                                                                : {
                                                                    border: "2px solid #28a745",
                                                                    backgroundColor: "#d4edda",
                                                                    color: "#155724",
                                                                    cursor: "pointer",
                                                                    textAlign: "center",
                                                                    verticalAlign: "middle",
                                                                    padding: "4px",
                                                                    whiteSpace: "normal",
                                                                    wordBreak: "break-word",
                                                                    overflowWrap: "break-word"
                                                                  };

                                                            return (
                                                                <td 
                                                                    key={day.dateStr} 
                                                                    style={cellStyle}
                                                                    onClick={() => setSelectedRequest(overlap)}
                                                                    title={`${overlap.leaveType?.name || 'Leave'}: ${overlap.reason}`}
                                                                >
                                                                    <span style={{ fontSize: '0.75rem', fontWeight: 'bold', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                                                                        {overlap.leaveType?.name || t('ess.away') || 'Away'}
                                                                    </span>
                                                                </td>
                                                            );
                                                        })}
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </Table>
                                ) : (
                                    <div style={{ height: '90vh', overflowY: 'auto' }}>
                                        <Table responsive hover striped id="usersListTable" style={{ margin: 0 }}>
                                            <thead className="bg-light sticky-top" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                                <tr>
                                                    <th>{t('ess.employee') || 'Employee'}</th>
                                                    <th>{t('ess.role') || 'Role'}</th>
                                                    <th>{t('ess.warehouse') || 'Warehouse'}</th>
                                                    <th>{t('ess.specialty') || 'Specialty'}</th>
                                                    <th>{t('ess.company') || 'Company'}</th>
                                                    <th>{t('ess.manager') || 'Manager'}</th>
                                                    <th>{t('ess.leaveDays') || 'Leave Days'}</th>
                                                    <th>Status</th>
                                                    <th>{t('ess.actions') || 'Actions'}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredEmployees.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={9} className="text-center text-muted py-4">
                                                            {t('ess.noEmployeesFound') || 'No employees found matching filters.'}
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    filteredEmployees.map(emp => (
                                                        <tr key={emp.userId}>
                                                            <td>
                                                                <strong>{emp.name}</strong>
                                                                <div className="text-muted small">@{emp.username}</div>
                                                            </td>
                                                            <td>
                                                                <Badge color="info">{emp.role}</Badge>
                                                                {emp.role2 && <Badge color="secondary" className="ms-1 ml-1">{emp.role2}</Badge>}
                                                            </td>
                                                            <td>{getWarehouseLabel(emp.whs)}</td>
                                                            <td>{emp.specialty || '-'}</td>
                                                            <td>{emp.company || '-'}</td>
                                                            <td>{emp.manager || '-'}</td>
                                                            <td>{emp.totalLeaveDays !== undefined ? emp.totalLeaveDays : '-'}</td>
                                                            <td>
                                                                {emp.isDeactivated ? (
                                                                    <Badge color="danger">Deactivated</Badge>
                                                                ) : (
                                                                    <Badge color="success">Active</Badge>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <Button
                                                                    size="sm"
                                                                    color="primary"
                                                                    outline
                                                                    className="me-1 mr-1"
                                                                    onClick={() => {
                                                                        setHistoryUserId(emp.userId);
                                                                        setActiveTab('history');
                                                                        setMainTab('requests');
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon icon={faHistory} className="me-1 mr-1" />
                                                                    {t('ess.history') || 'History'}
                                                                </Button>
                                                                {isHrOrAdmin() && (
                                                                    <Button
                                                                        size="sm"
                                                                        color={emp.isDeactivated ? "success" : "danger"}
                                                                        outline
                                                                        onClick={() => {
                                                                            const updated = { ...emp, isDeactivated: !emp.isDeactivated };
                                                                            usersService.updateUser(updated).then(() => {
                                                                                addToast(emp.isDeactivated ? "User activated" : "User deactivated", { appearance: 'success', autoDismiss: true });
                                                                                fetchData();
                                                                            }).catch(e => addToast(e, { appearance: 'error', autoDismiss: true }));
                                                                        }}
                                                                    >
                                                                        {emp.isDeactivated ? "Activate" : "Deactivate"}
                                                                    </Button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    ) : (
                        <Card>
                            <CardHeader className="d-flex justify-content-between align-items-center flex-wrap pb-0">
                                <Nav tabs className="border-bottom-0">
                                    <NavItem>
                                        <NavLink
                                            className={activeTab === 'pending' ? 'active font-weight-bold' : ''}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => setActiveTab('pending')}
                                        >
                                            {t('ess.pending') || 'Pending'} ({totalPending})
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink
                                            className={activeTab === 'leaves' ? 'active font-weight-bold' : ''}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => setActiveTab('leaves')}
                                        >
                                            {t('ess.leaveRequests') || 'Leave Requests'} ({filteredLeaveRequests.length})
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink
                                            className={activeTab === 'advances' ? 'active font-weight-bold' : ''}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => setActiveTab('advances')}
                                        >
                                            {t('ess.depositRequests') || 'Deposit Requests'} ({filteredAdvanceRequests.length})
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink
                                            className={activeTab === 'history' ? 'active font-weight-bold' : ''}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => setActiveTab('history')}
                                        >
                                            {t('ess.requestHistory') || 'Request History'}
                                        </NavLink>
                                    </NavItem>
                                </Nav>

                                <div className="mb-2">
                                    {activeTab === 'pending' && (
                                        <ReactHTMLTableToExcel
                                            id="pendingExcelBtn"
                                            className="btn btn-sm btn-success fw-bold"
                                            table="pendingRequestsTable"
                                            filename="pending_requests"
                                            sheet="pending"
                                            buttonText={t('ess.exportExcel') || 'Export to Excel'}
                                        />
                                    )}
                                    {activeTab === 'leaves' && (
                                        <ReactHTMLTableToExcel
                                            id="leavesExcelBtn"
                                            className="btn btn-sm btn-success fw-bold"
                                            table="leaveRequestsTable"
                                            filename="leave_requests"
                                            sheet="leaves"
                                            buttonText={t('ess.exportExcel') || 'Export to Excel'}
                                        />
                                    )}
                                    {activeTab === 'advances' && (
                                        <ReactHTMLTableToExcel
                                            id="advancesExcelBtn"
                                            className="btn btn-sm btn-success fw-bold"
                                            table="advanceRequestsTable"
                                            filename="deposit_advance_requests"
                                            sheet="deposits_advances"
                                            buttonText={t('ess.exportExcel') || 'Export to Excel'}
                                        />
                                    )}
                                    {activeTab === 'history' && (
                                        <ReactHTMLTableToExcel
                                            id="historyExcelBtn"
                                            className="btn btn-sm btn-success fw-bold"
                                            table="userHistoryTable"
                                            filename="user_request_history"
                                            sheet="history"
                                            buttonText={t('ess.exportExcel') || 'Export to Excel'}
                                        />
                                    )}
                                </div>
                            </CardHeader>
                            <CardBody className="p-0">
                                <TabContent activeTab={activeTab}>
                                        {/* TAB 1: PENDING REQUESTS */}
                                        <TabPane tabId="pending">
                                            <Table hover responsive id="pendingRequestsTable" style={{ margin: 0 }}>
                                                <thead className="bg-light">
                                                    <tr>
                                                        <th>{t('ess.employee') || 'Employee'}</th>
                                                        <th>{t('ess.type') || 'Type'}</th>
                                                        <th>{t('ess.details') || 'Details'}</th>
                                                        <th>{t('ess.requestedAt') || 'Requested At'}</th>
                                                        <th>{t('ess.actions') || 'Actions'}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {totalPending === 0 ? (
                                                        <tr>
                                                            <td colSpan={5} className="text-center text-muted py-4">
                                                                {t('ess.noPendingRequests') || 'No pending requests found.'}
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        <>
                                                            {/* Leaves */}
                                                            {pendingLeaves.map(lr => (
                                                                <tr key={`leave-${lr.id}`} style={{ cursor: 'pointer' }} onClick={() => setSelectedRequest(lr)}>
                                                                    <td>
                                                                        <strong>{lr.user?.name}</strong>
                                                                        <div className="text-muted" style={{ fontSize: '0.8rem' }}>{lr.specialty}</div>
                                                                    </td>
                                                                    <td>
                                                                        <Badge color="warning">{t('ess.leave')?.toUpperCase() || 'LEAVE'}</Badge>
                                                                    </td>
                                                                    <td>
                                                                        <div>{lr.leaveType?.name}</div>
                                                                        <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                                                                            {formatGreekDate(lr.startDate)} {t('ess.to') || 'to'} {formatGreekDate(lr.endDate)}
                                                                        </div>
                                                                    </td>
                                                                    <td>{formatGreekDate(lr.requestedAt)}</td>
                                                                    <td>
                                                                        <Button size="sm" color="primary" onClick={(e) => { e.stopPropagation(); setSelectedRequest(lr); }}>
                                                                            {t('ess.review') || 'Review'}
                                                                        </Button>
                                                                    </td>
                                                                </tr>
                                                            ))}

                                                            {/* Advances */}
                                                            {pendingAdvances.map(ar => (
                                                                <tr key={`advance-${ar.id}`} style={{ cursor: 'pointer' }} onClick={() => setSelectedRequest(ar)}>
                                                                    <td>
                                                                        <strong>{ar.user?.name}</strong>
                                                                        <div className="text-muted" style={{ fontSize: '0.8rem' }}>{ar.user?.role}</div>
                                                                    </td>
                                                                    <td>
                                                                        <Badge color="success">{t('ess.advance')?.toUpperCase() || 'ADVANCE'}</Badge>
                                                                    </td>
                                                                    <td>
                                                                        <strong>€{parseFloat(ar.amount).toFixed(2)}</strong>
                                                                        <div className="text-muted text-truncate" style={{ fontSize: '0.8rem', maxWidth: '200px' }}>
                                                                            {ar.notes || 'No notes'}
                                                                        </div>
                                                                    </td>
                                                                    <td>{formatGreekDate(ar.requestedAt)}</td>
                                                                    <td>
                                                                        <Button size="sm" color="primary" onClick={(e) => { e.stopPropagation(); setSelectedRequest(ar); }}>
                                                                            {t('ess.review') || 'Review'}
                                                                        </Button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </>
                                                    )}
                                                </tbody>
                                            </Table>
                                        </TabPane>

                                        {/* TAB 2: ALL LEAVE REQUESTS */}
                                        <TabPane tabId="leaves">
                                            <div className="p-3 bg-light border-bottom">
                                                <Row className="g-2 align-items-center">
                                                    <Col md={6}>
                                                        <Input
                                                            type="text"
                                                            placeholder={t('ess.searchLeavePlaceholder') || "Search by employee, leave type, reason..."}
                                                            value={leaveSearch}
                                                            onChange={e => setLeaveSearch(e.target.value)}
                                                            bsSize="sm"
                                                        />
                                                    </Col>
                                                    <Col md={4}>
                                                        <Input
                                                            type="select"
                                                            value={leaveStatusFilter}
                                                            onChange={e => setLeaveStatusFilter(e.target.value)}
                                                            bsSize="sm"
                                                        >
                                                            <option value="all">{t('ess.allStatuses') || 'All Statuses'}</option>
                                                            <option value="pending">{t('ess.pending') || 'Pending'}</option>
                                                            <option value="approved">{t('ess.approved') || 'Approved'}</option>
                                                            <option value="rejected">{t('ess.rejected') || 'Rejected'}</option>
                                                            <option value="cancelled">{t('ess.cancelled') || 'Cancelled'}</option>
                                                        </Input>
                                                    </Col>
                                                    <Col md={2} className="text-end text-muted small">
                                                        {t('ess.showing') 
                                                            ? t('ess.showing').replace('{0}', filteredLeaveRequests.length).replace('{1}', leaveRequests.length)
                                                            : `Showing ${filteredLeaveRequests.length} of ${leaveRequests.length}`}
                                                    </Col>
                                                </Row>
                                            </div>
                                            <Table hover responsive id="leaveRequestsTable" style={{ margin: 0 }}>
                                                <thead className="bg-light">
                                                    <tr>
                                                        <th>{t('ess.employee') || 'Employee'}</th>
                                                        <th>{t('ess.leaveType') || 'Leave Type'}</th>
                                                        <th>{t('ess.startDate') || 'Start Date'}</th>
                                                        <th>{t('ess.endDate') || 'End Date'}</th>
                                                        <th>{t('ess.reason') || 'Reason'}</th>
                                                        <th>{t('ess.status') || 'Status'}</th>
                                                        <th>{t('ess.rejectionReason') || 'Rejection Reason'}</th>
                                                        <th>{t('ess.requestedAt') || 'Requested At'}</th>
                                                        <th>{t('ess.actions') || 'Actions'}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredLeaveRequests.length === 0 ? (
                                                        <tr>
                                                            <td colSpan={9} className="text-center text-muted py-4">
                                                                {t('ess.noLeaveRequestsMatch') || 'No leave requests found matching filters.'}
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        filteredLeaveRequests.map(lr => (
                                                            <tr key={`leave-all-${lr.id}`} style={{ cursor: 'pointer' }} onClick={() => setSelectedRequest(lr)}>
                                                                <td>
                                                                    <strong>{lr.user?.name || 'N/A'}</strong>
                                                                    <div className="text-muted" style={{ fontSize: '0.8rem' }}>{lr.specialty || lr.user?.specialty || ''}</div>
                                                                </td>
                                                                <td>{lr.leaveType?.name || t('ess.leave') || 'Leave'}</td>
                                                                <td>{formatGreekDate(lr.startDate)}</td>
                                                                <td>{formatGreekDate(lr.endDate)}</td>
                                                                <td>{lr.reason || '-'}</td>
                                                                <td>
                                                                    <Badge color={getStatusBadgeColor(lr.status)}>
                                                                        {lr.status.toUpperCase()}
                                                                    </Badge>
                                                                </td>
                                                                <td>{lr.rejectionReason || '-'}</td>
                                                                <td>{formatGreekDate(lr.requestedAt)}</td>
                                                                <td>
                                                                    <Button size="sm" color="primary" onClick={(e) => { e.stopPropagation(); setSelectedRequest(lr); }}>
                                                                        {t('ess.review') || 'Review'}
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </Table>
                                        </TabPane>

                                        {/* TAB 3: ALL DEPOSIT / ADVANCE REQUESTS */}
                                        <TabPane tabId="advances">
                                            <div className="p-3 bg-light border-bottom">
                                                <Row className="g-2 align-items-center">
                                                    <Col md={6}>
                                                        <Input
                                                            type="text"
                                                            placeholder={t('ess.searchAdvancePlaceholder') || "Search by employee, notes, comment, amount..."}
                                                            value={advanceSearch}
                                                            onChange={e => setAdvanceSearch(e.target.value)}
                                                            bsSize="sm"
                                                        />
                                                    </Col>
                                                    <Col md={4}>
                                                        <Input
                                                            type="select"
                                                            value={advanceStatusFilter}
                                                            onChange={e => setAdvanceStatusFilter(e.target.value)}
                                                            bsSize="sm"
                                                        >
                                                            <option value="all">{t('ess.allStatuses') || 'All Statuses'}</option>
                                                            <option value="pending">{t('ess.pending') || 'Pending'}</option>
                                                            <option value="approved">{t('ess.approved') || 'Approved'}</option>
                                                            <option value="rejected">{t('ess.rejected') || 'Rejected'}</option>
                                                            <option value="cancelled">{t('ess.cancelled') || 'Cancelled'}</option>
                                                        </Input>
                                                    </Col>
                                                    <Col md={2} className="text-end text-muted small">
                                                        {t('ess.showing') 
                                                            ? t('ess.showing').replace('{0}', filteredAdvanceRequests.length).replace('{1}', advanceRequests.length)
                                                            : `Showing ${filteredAdvanceRequests.length} of ${advanceRequests.length}`}
                                                    </Col>
                                                </Row>
                                            </div>
                                            <Table hover responsive id="advanceRequestsTable" style={{ margin: 0 }}>
                                                <thead className="bg-light">
                                                    <tr>
                                                        <th>{t('ess.employee') || 'Employee'}</th>
                                                        <th>{t('ess.amount') || 'Amount (€)'}</th>
                                                        <th>{t('ess.months') || 'Months'}</th>
                                                        <th>{t('ess.amountPerMonth') || 'Amount/Month (€)'}</th>
                                                        <th>{t('ess.paymentComment') || 'Payment Comment'}</th>
                                                        <th>{t('ess.notes') || 'Notes'}</th>
                                                        <th>{t('ess.status') || 'Status'}</th>
                                                        <th>{t('ess.rejectionReason') || 'Rejection Reason'}</th>
                                                        <th>{t('ess.requestedAt') || 'Requested At'}</th>
                                                        <th>{t('ess.actions') || 'Actions'}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredAdvanceRequests.length === 0 ? (
                                                        <tr>
                                                            <td colSpan={10} className="text-center text-muted py-4">
                                                                {t('ess.noAdvanceRequestsMatch') || 'No deposit/advance requests found matching filters.'}
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        filteredAdvanceRequests.map(ar => (
                                                            <tr key={`advance-all-${ar.id}`} style={{ cursor: 'pointer' }} onClick={() => setSelectedRequest(ar)}>
                                                                <td>
                                                                    <strong>{ar.user?.name || 'N/A'}</strong>
                                                                    <div className="text-muted" style={{ fontSize: '0.8rem' }}>{ar.user?.role || ''}</div>
                                                                </td>
                                                                <td><strong>€{parseFloat(ar.amount).toFixed(2)}</strong></td>
                                                                <td>{ar.months || '-'}</td>
                                                                <td>{ar.amountPerMonth ? `€${parseFloat(ar.amountPerMonth).toFixed(2)}` : '-'}</td>
                                                                <td>{ar.paymentComment || '-'}</td>
                                                                <td>{ar.notes || '-'}</td>
                                                                <td>
                                                                    <Badge color={getStatusBadgeColor(ar.status)}>
                                                                        {ar.status.toUpperCase()}
                                                                    </Badge>
                                                                </td>
                                                                <td>{ar.rejectionReason || '-'}</td>
                                                                <td>{formatGreekDate(ar.requestedAt)}</td>
                                                                <td>
                                                                    <Button size="sm" color="primary" onClick={(e) => { e.stopPropagation(); setSelectedRequest(ar); }}>
                                                                        {t('ess.review') || 'Review'}
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </Table>
                                        </TabPane>

                                        {/* TAB 4: REQUEST HISTORY */}
                                        <TabPane tabId="history">
                                            <div className="p-3 bg-light border-bottom">
                                                <Row className="g-2 align-items-center">
                                                    <Col md={6}>
                                                        <Label className="small mb-1 font-weight-bold">{t('ess.selectEmployee') || 'Select Employee:'}</Label>
                                                        <Input
                                                            type="select"
                                                            value={historyUserId}
                                                            onChange={e => setHistoryUserId(e.target.value)}
                                                            bsSize="sm"
                                                        >
                                                            <option value="all">{t('ess.allFilteredEmployees') || 'All Filtered Employees'}</option>
                                                            {filteredEmployees.map(emp => (
                                                                <option key={emp.userId} value={emp.userId}>{emp.name} ({emp.specialty || t('ess.noSpecialty') || 'No Specialty'})</option>
                                                            ))}
                                                        </Input>
                                                    </Col>
                                                </Row>
                                            </div>
                                            <Table hover responsive id="userHistoryTable" style={{ margin: 0 }}>
                                                <thead className="bg-light">
                                                    <tr>
                                                        <th>{t('ess.employee') || 'Employee'}</th>
                                                        <th>{t('ess.requestType') || 'Request Type'}</th>
                                                        <th>{t('ess.detailsPeriod') || 'Details / Period'}</th>
                                                        <th>{t('ess.status') || 'Status'}</th>
                                                        <th>{t('ess.reasonNotes') || 'Reason / Notes'}</th>
                                                        <th>{t('ess.requestedAt') || 'Requested At'}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(() => {
                                                        const targetEmployees = historyUserId === 'all' 
                                                            ? filteredEmployees 
                                                            : filteredEmployees.filter(e => e.userId.toString() === historyUserId.toString());
                                                        
                                                        const targetUserIds = new Set(targetEmployees.map(e => e.userId));
                                                        
                                                        const userLeaves = leaveRequests.filter(lr => lr.userId && targetUserIds.has(lr.userId));
                                                        const userAdvances = advanceRequests.filter(ar => ar.userId && targetUserIds.has(ar.userId));
                                                        
                                                        const combined = [
                                                            ...userLeaves.map(lr => ({ ...lr, isLeave: true })),
                                                            ...userAdvances.map(ar => ({ ...ar, isLeave: false }))
                                                        ].sort((a, b) => new Date(b.requestedAt) - new Date(a.requestedAt));

                                                        if (combined.length === 0) {
                                                            return (
                                                                <tr>
                                                                    <td colSpan={6} className="text-center text-muted py-4">
                                                                        {t('ess.noHistory') || 'No request history found for selected employee(s).'}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        }

                                                        return combined.map(req => (
                                                            <tr key={`history-${req.isLeave ? 'leave' : 'advance'}-${req.id}`}>
                                                                <td>
                                                                    <strong>{req.user?.name || 'N/A'}</strong>
                                                                    <div className="text-muted" style={{ fontSize: '0.8rem' }}>{req.user?.specialty || ''}</div>
                                                                </td>
                                                                <td>
                                                                    <Badge color={req.isLeave ? 'warning' : 'success'}>
                                                                        {req.isLeave ? (t('ess.leave')?.toUpperCase() || 'LEAVE') : (t('ess.advance')?.toUpperCase() || 'ADVANCE')}
                                                                    </Badge>
                                                                </td>
                                                                <td>
                                                                    {req.isLeave ? (
                                                                        <div>
                                                                            <div>{req.leaveType?.name}</div>
                                                                            <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                                                                                {formatGreekDate(req.startDate)} {t('ess.to') || 'to'} {formatGreekDate(req.endDate)}
                                                                            </div>
                                                                        </div>
                                                                    ) : (
                                                                        <div>
                                                                            <strong>€{parseFloat(req.amount).toFixed(2)}</strong>
                                                                            {req.months ? <div className="text-muted" style={{ fontSize: '0.8rem' }}>{req.months} {t('ess.monthsText') || 'months'}</div> : null}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <Badge color={getStatusBadgeColor(req.status)}>
                                                                        {req.status.toUpperCase()}
                                                                    </Badge>
                                                                </td>
                                                                <td>{req.isLeave ? (req.reason || '-') : (req.notes || req.paymentComment || '-')}</td>
                                                                <td>{formatGreekDate(req.requestedAt)}</td>
                                                            </tr>
                                                        ));
                                                    })()}
                                                </tbody>
                                            </Table>
                                        </TabPane>
                                    </TabContent>
                                </CardBody>
                            </Card>
                        )
                    }
                </Col>

                {/* Sidebar approval/headroom panel */}
                <Col lg={4} md={12}>
                    <Card>
                        <CardHeader className="bg-primary text-white d-flex justify-content-between align-items-center">
                            <strong>{t('ess.approvalHeadroomPane') || 'Approval & Headroom Pane'}</strong>
                            {selectedRequest && (
                                <button 
                                    type="button" 
                                    className="btn btn-sm text-white" 
                                    style={{ padding: '0 4px', fontSize: '1.2rem', lineHeight: 1 }} 
                                    onClick={() => setSelectedRequest(null)}
                                    title="Close panel"
                                >
                                    &times;
                                </button>
                            )}
                        </CardHeader>
                        <CardBody>
                            {!selectedRequest ? (
                                <div className="text-center text-muted py-5">
                                    {t('ess.selectRequestPrompt') || 'Select a request from the timeline or pending list to review.'}
                                </div>
                            ) : (
                                <div>
                                    <h4 className="mb-3">
                                        {t('ess.reviewRequest') || 'Review Request'}
                                        <Badge color={selectedRequest.status === 'approved' ? 'success' : selectedRequest.status === 'rejected' ? 'danger' : 'warning'} className="ml-2 float-right">
                                            {selectedRequest.status.toUpperCase()}
                                        </Badge>
                                    </h4>
                                    
                                    <div className="mb-4">
                                        <p><strong>{t('ess.employee') || 'Employee'}:</strong> {selectedRequest.user?.name || 'N/A'}</p>
                                        <p><strong>{t('ess.specialty') || 'Specialty'}:</strong> {selectedRequest.specialty || selectedRequest.user?.specialty || t('ess.noSpecialty') || 'None'}</p>
                                        
                                        {selectedRequest.leaveTypeId ? (
                                            <>
                                                <p><strong>{t('ess.type') || 'Type'}:</strong> {t('ess.leave') || 'Leave'} ({selectedRequest.leaveType?.name})</p>
                                                <p><strong>{t('ess.period') || 'Period'}:</strong> {formatGreekDate(selectedRequest.startDate)} {t('ess.to') || 'to'} {formatGreekDate(selectedRequest.endDate)}</p>
                                                <p><strong>{t('ess.reason') || 'Reason'}:</strong> {selectedRequest.reason}</p>
                                                {selectedRequest.documentUrl && (
                                                    <p><strong>{t('ess.document') || 'Document'}:</strong> <a href={`http://192.168.34.100:3200/uploads/${selectedRequest.documentUrl}`} target="_blank" rel="noopener noreferrer">{t('ess.viewAttachedDocument') || 'View Attached Document'}</a></p>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                <p><strong>{t('ess.type') || 'Type'}:</strong> {t('ess.cashAdvance') || 'Cash Advance'}</p>
                                                <p><strong>{t('ess.amount') || 'Amount'}:</strong> €{parseFloat(selectedRequest.amount).toFixed(2)}</p>
                                                {selectedRequest.months && <p><strong>{t('ess.monthsOfRepayment') || 'Months of repayment'}:</strong> {selectedRequest.months}</p>}
                                                {selectedRequest.amountPerMonth && <p><strong>{t('ess.amountPerMonthLabel') || 'Amount per month'}:</strong> €{parseFloat(selectedRequest.amountPerMonth).toFixed(2)}</p>}
                                                {selectedRequest.paymentComment && <p><strong>{t('ess.paymentComment') || 'Payment Comment'}:</strong> {selectedRequest.paymentComment}</p>}
                                                {selectedRequest.paymentMethod && (
                                                    <p><strong>{t('ess.paymentMethod') || 'Payment Method'}:</strong> {selectedRequest.paymentMethod === 'salary' ? (t('ess.salaryDeduction') || 'Salary Deduction') : selectedRequest.paymentMethod === 'cashier' ? (t('ess.cashierPayment') || 'Cashier Payment') : selectedRequest.paymentMethod}</p>
                                                )}
                                                <p><strong>{t('ess.notes') || 'Notes'}:</strong> {selectedRequest.notes || 'N/A'}</p>
                                            </>
                                        )}
                                    </div>

                                    {/* Specialty Headcount Feedback Panel */}
                                    {selectedRequest.leaveTypeId && (
                                        <div className="mb-4">
                                            <h5 className="border-bottom pb-2">{t('ess.specialtyHeadroomAnalysis') || 'Specialty Headroom Analysis'}</h5>
                                            {loadingHeadroom ? (
                                                <div className="text-center text-muted py-3">{t('ess.calculatingHeadroom') || 'Calculating headroom...'}</div>
                                            ) : headroomData.length === 0 ? (
                                                <div className="text-muted small">{t('ess.noHeadroomData') || 'No headroom data available.'}</div>
                                            ) : (
                                                <Table responsive bordered size="sm" style={{ fontSize: '0.8rem' }}>
                                                    <thead className="bg-light">
                                                        <tr>
                                                            <th>{t('ess.date') || 'Date'}</th>
                                                            <th className="text-center">{t('ess.total') || 'Total'}</th>
                                                            <th className="text-center">{t('ess.away') || 'Away'}</th>
                                                            <th className="text-center">{t('ess.left') || 'Left'}</th>
                                                            <th className="text-center">{t('ess.sim') || 'Sim'}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {headroomData.map(row => (
                                                            <tr key={row.date}>
                                                                <td>{formatGreekDate(row.date)}</td>
                                                                <td className="text-center">{row.total}</td>
                                                                <td className="text-center text-danger">{row.approvedCount}</td>
                                                                <td className="text-center font-weight-bold">{row.remaining}</td>
                                                                <td className="text-center text-primary font-weight-bold" style={{ backgroundColor: '#e6f2ff' }}>
                                                                    {row.simulated}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            )}
                                        </div>
                                    )}

                                    {selectedRequest.status === 'pending' && (
                                        <Form onSubmit={(e) => e.preventDefault()} className="border-top pt-3">
                                            <FormGroup>
                                                <Label for="rejectionReason">{t('ess.rejectionReason') || 'Rejection Reason'}</Label>
                                                <Input 
                                                    type="textarea" 
                                                    id="rejectionReason" 
                                                    rows={3} 
                                                    placeholder={t('ess.rejectionReasonPlaceholder') || "Required only if rejecting..."}
                                                    value={rejectionReason}
                                                    onChange={(e) => setRejectionReason(e.target.value)}
                                                />
                                            </FormGroup>

                                            <div className="d-flex justify-content-between mt-3">
                                                <Button color="danger" className="w-45" onClick={() => handleReject(selectedRequest)}>
                                                    {t('ess.reject') || 'Reject'}
                                                </Button>
                                                <Button color="success" className="w-45" onClick={() => handleApprove(selectedRequest)}>
                                                    {t('ess.approve') || 'Approve'}
                                                </Button>
                                            </div>
                                        </Form>
                                    )}
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default withTranslation(EssManager);
